import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import { ListSort } from "@gen/tracker/v1/common_pb";

/**
 * How a screen reads a catalog (offers, landers, domains…) that is larger than
 * any one response.
 *
 * The API serves at most PAGE_MAX rows per call and always says how many
 * there are in all (`pagination.total`). Two things follow, and every screen
 * keeps to them rather than asking for «the first N» and treating them as the
 * whole catalog — which is how «1–50 из 200» came to stand for 868 offers:
 *
 *   - a list shows a page the SERVER cut: search, filters, order and the
 *     window go to the API, the total is the API's;
 *   - a screen that holds ids (a campaign's landers, a report's rows) names
 *     them by asking for exactly those ids — `byIds` — never by looking them
 *     up in a page of the catalog.
 */
export const PAGE_MAX = 500;

/**
 * The table's sort as the API's `ListSort`. `fields` maps a column id to the
 * field the server sorts by; a column it does not name is not sortable on a
 * server-paged list (sorting one page of it would be a lie), so it yields the
 * list's default order. Pure.
 */
export function listSort(sorting: SortingState | undefined, fields: Record<string, string>): ListSort | undefined {
  const s = sorting?.[0];
  const field = s ? fields[s.id] : undefined;
  return s && field ? new ListSort({ field, desc: s.desc }) : undefined;
}

/** `ids` split into requests the API accepts. Pure. */
export function idChunks(ids: Iterable<string>, size = PAGE_MAX): string[][] {
  const unique = [...new Set([...ids].filter(Boolean))].sort();
  const out: string[][] = [];
  for (let i = 0; i < unique.length; i += size) out.push(unique.slice(i, i + size));
  return out;
}

/** Every row a paged endpoint holds, read PAGE_MAX at a time. */
export async function fetchAll<T>(page: (offset: number, limit: number) => Promise<{ items: T[]; total: number }>): Promise<{ items: T[]; total: number }> {
  const first = await page(0, PAGE_MAX);
  const items = [...first.items];
  for (let offset = PAGE_MAX; offset < first.total && items.length < first.total; offset += PAGE_MAX) {
    const next = await page(offset, PAGE_MAX);
    if (next.items.length === 0) break;
    items.push(...next.items);
  }
  return { items, total: first.total };
}

/**
 * The rows with these ids, keyed by id — one request per PAGE_MAX ids. An id
 * the catalog does not hold is simply absent from the map. `scope` is the
 * service's own query-key root, so the mutations that refresh its lists
 * refresh these names too.
 */
export function useByIds<T extends { id: string }>(
  scope: readonly unknown[],
  ids: Iterable<string>,
  fetchChunk: (chunk: string[]) => Promise<T[]>,
  enabled = true,
) {
  const chunks = idChunks(ids);
  return useQuery({
    queryKey: [...scope, "by-ids", chunks] as const,
    enabled: enabled && chunks.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    queryFn: async (): Promise<Map<string, T>> => {
      const parts = await Promise.all(chunks.map(fetchChunk));
      return new Map(parts.flat().map((row) => [row.id, row] as const));
    },
  });
}
